import React, { useEffect, useState } from 'react';
import InnerLayout from '../../components/layouts/innerLayout';
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import SelectDropdown from '../../components/forms/selectDropDown';
import { formatString, generateYearsList } from '../../components/helper';
import LoaderModal from '../../components/modals/loaderModal';
import { useDispatch } from 'react-redux';
import { dashboardAction, dashboardMontlySalesAction } from '../../redux/actions/dashboardAction';
import { errorToast } from '../../components/toast';
import moment from 'moment';

const Dashboard = () => {

    const salesData = [
        { month: "Jan", sales: 0 },
        { month: "Feb", sales: 0 },
        { month: "Mar", sales: 0 },
        { month: "Apr", sales: 0 },
        { month: "May", sales: 0 },
        { month: "Jun", sales: 0 },
        { month: "Jul", sales: 0 },
        { month: "Aug", sales: 0 },
        { month: "Sep", sales: 0 },
        { month: "Oct", sales: 0 },
        { month: "Nov", sales: 0 },
        { month: "Dec", sales: 0 },
    ];

    const dispatch = useDispatch();
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState({});
    const [monthlySalesData, setMonthlySalesData] = useState(salesData);
    const yearOptions = generateYearsList();

    useEffect(() => {
        dispatch(dashboardAction({}, onSuccess, onError));
    }, [])

    useEffect(() => {
        dispatch(dashboardMontlySalesAction({ year: year }, onMonthlySalesSuccess, onError))
    }, [year])

    const onSuccess = (data) => {
        setDashboardData(data.data.data);
        setLoading(false)
    }

    const onMonthlySalesSuccess = (data) => {
        setMonthlySalesData(data.data.data);
        setLoading(false)
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
        setLoading(false)
    }

    return (
        <InnerLayout module={'Dashboard'}>
            <div className="p-4">
                {/* Key Metrics Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
                    {/* Total Sales */}
                    <div className="bg-white p-6 rounded-lg shadow-md flex justify-between items-center">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-700">Total Sales</h3>
                            <p className="text-2xl font-bold text-primary">₹ {dashboardData?.totalSales}</p>
                        </div>
                        <div className="text-4xl text-primary">
                            <i className="fas fa-dollar-sign"></i>
                        </div>
                    </div>

                    {/* Total Products */}
                    <div className="bg-white p-6 rounded-lg shadow-md flex justify-between items-center">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-700">Total Products</h3>
                            <p className="text-2xl font-bold text-primary">{dashboardData?.totalProducts}</p>
                        </div>
                        <div className="text-4xl text-primary">
                            <i className="fas fa-cogs"></i>
                        </div>
                    </div>

                    {/* Total Invoices */}
                    <div className="bg-white p-6 rounded-lg shadow-md flex justify-between items-center">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-700">Total Invoices</h3>
                            <p className="text-2xl font-bold text-primary">{dashboardData?.totalInvoices}</p>
                        </div>
                        <div className="text-4xl text-primary">
                            <i className="fas fa-file-invoice"></i>
                        </div>
                    </div>
                </div>

                {/* Recent Activity Section */}
                <div className='flex flex-wrap md:flex-nowrap w-full justify-evenly gap-4'>
                    <div className="bg-white p-6 rounded-lg shadow-md mb-6 w-full">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4 text-start">Recent Activity</h2>
                        <ul className="space-y-4">
                            {/* <li className="flex justify-between text-sm text-gray-600">
                            <span>New Order: #{dashboardData?.latestInvoice?.code}</span>
                            <span className="font-medium text-primary">+ ₹{dashboardData?.latestInvoice?.grandTotal}</span>
                        </li> */}
                            <li className="flex justify-between text-sm text-gray-600">
                                <span>Invoice #{dashboardData?.latestInvoice?.code} Created</span>
                                <span className="font-medium text-primary">+ ₹{dashboardData?.latestInvoice?.grandTotal}</span>
                            </li>
                            <li className="flex justify-between text-sm text-gray-600">
                                <span>Product Added: {formatString(dashboardData?.latestProduct?.name)}</span>
                                <span className="font-medium text-primary">+</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md mb-6 w-full">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4 text-start">Active Subscription</h2>
                        <ul className="space-y-4">
                            <li className="flex justify-between text-sm text-gray-600">
                                <span>#Subscribed: {dashboardData?.activeSubscription?.purchased_plan?.name ?? "Free Trail"}</span>
                                <span className="font-medium text-primary">₹{dashboardData?.activeSubscription?.purchased_plan?.price}</span>
                            </li>
                            <li className="flex justify-between text-sm text-gray-600">
                                <span className="font-medium text-primary">Start Date: {dashboardData?.activeSubscription?.start_date ? moment(dashboardData?.activeSubscription?.start_date).format('DD-MM-YYYY') : "--"}</span>
                                <span className="font-medium text-danger">End Date: {dashboardData?.activeSubscription?.end_date ? moment(dashboardData?.activeSubscription?.end_date).format('DD-MM-YYYY') : "--"}</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md mb-6 w-full">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4 text-start">Upcoming Subscription</h2>
                        <ul className="space-y-4">
                            <li className="flex justify-between text-sm text-gray-600">
                                <span>#Subscribed: {dashboardData?.upcomingSubscription?.purchased_plan?.name ?? "--"}</span>
                                <span className="font-medium text-primary">₹{dashboardData?.upcomingSubscription?.purchased_plan?.price ?? "--"}</span>
                            </li>
                            <li className="flex justify-between text-sm text-gray-600">
                                <span className="font-medium text-primary">Start Date: {dashboardData?.upcomingSubscription?.start_date ? moment(dashboardData?.upcomingSubscription?.start_date).format('DD-MM-YYYY') : "--"}</span>
                                <span className="font-medium text-danger">End Date: {dashboardData?.upcomingSubscription?.end_date ? moment(dashboardData?.upcomingSubscription?.end_date).format('DD-MM-YYYY') : "--"}</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Optional: Sales Graph (Placeholder) */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <motion.div
                        className="bg-white rounded shadow p-4 h-full"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                    >
                        <h2 className="text-xl font-bold text-primary mb-2 text-start">Monthly Sales</h2>
                        <div className="mb-2 flex justify-end gap-3 items-center">
                            <label htmlFor="year" className="block font-medium text-primary">Year</label>
                            <div className="w-full max-w-28 sm:max-w-28 md:w-28">
                                <SelectDropdown
                                    options={yearOptions}
                                    value={year}
                                    onChange={(name, value) => setYear(value)}
                                    placeholder="Select year"
                                    name="category_id"
                                    className="input-field"
                                    isFormik={false}
                                />
                            </div>
                        </div>

                        <ResponsiveContainer width="100%" height={350}>
                            <LineChart data={monthlySalesData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="sales"
                                    stroke="#2563EB"
                                    activeDot={{ r: 8 }}
                                    isAnimationActive={true}
                                    animationDuration={1500}
                                    animationEasing="ease-in-out"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </motion.div>
                </div>
                {loading &&
                    <LoaderModal />
                }
            </div>
        </InnerLayout>
    );
};


export default Dashboard;
